// EnhancedAuthCheck.js (clean, palette-aligned)
import React, { useState } from 'react';
import { X, Check, Zap, ChevronDown, Star } from 'lucide-react';
import logo from '../src/images/70Lives Beauty Ledger Logo.png';

const EnhancedAuthCheck = ({
  isOpen,
  onClose,
  onGoogleSignIn,
  showPricing = false,
  rememberMe = true,
  onRememberChange = () => {},
  onNavigate
}) => {
  const [currentView, setCurrentView] = useState(showPricing ? 'choose-plan' : 'signin');
  const [expandedPlan, setExpandedPlan] = useState(null);

  if (!isOpen) return null;

  const handleNavigate = (view) => {
    if (typeof onNavigate === 'function') {
      onNavigate(view);
      return;
    }
    setCurrentView(view);
  };

  const trialFeatures = [
    'Upload unlimited receipts',
    'Advanced OCR scanning',
    'Voice-to-text input',
    'Multi-currency support',
    'Advanced reporting',
    'Export to Excel, PDF',
    'Priority email support'
  ];

  const handleGoogleSignIn = async () => {
    try { await onGoogleSignIn(); onClose(); } catch (e) { console.error('Sign-in error:', e); }
  };
  const handleCreateFreeUser = async () => {
    try { await onGoogleSignIn('free'); onClose(); } catch (e) { console.error('Free signup error:', e); }
  };
  const handleCreateTrialUser = async () => {
    try { await onGoogleSignIn('trial'); onClose(); } catch (e) { console.error('Trial signup error:', e); }
  };

  const SignInView = () => (
    <div className="text-center">
      <div className="mb-6">
        <img src={logo} alt="70Lives Logo" className="h-16 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-[color:var(--color-text)] mb-2">Welcome to 70Lives</h2>
        <p className="text-[color:var(--color-text)]">Sign in to access your business finance tracker</p>
      </div>
      <button onClick={handleGoogleSignIn} className="w-full bg-[color:var(--color-warn)] text-[color:var(--color-text)] px-6 py-3 rounded-lg hover:brightness-95 transition flex items-center justify-center gap-3 mb-4">
        <svg className="w-6 h-6" viewBox="0 0 24 24"><path fill="currentColor" d="M12.545,12.151c0,1.054.855,1.909,1.909,1.909h3.536c-.607,1.972-2.405,3.404-4.536,3.404-2.626,0-4.754-2.128-4.754-4.754s2.128-4.754,4.754-4.754c1.26,0,2.405.492,3.255,1.29l1.534-1.534C16.455,6.892,14.426,6,12.545,6,8.901,6,6,8.901,6,12.545s2.901,6.545,6.545,6.545c4.017,0,6.545-2.927,6.545-6.545V11.5h-6.545v.651z"/></svg>
        Continue with Google
      </button>
      <label className="flex items-center justify-center gap-2 text-sm text-[color:var(--color-text)] mb-4">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => onRememberChange(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 accent-[color:var(--color-warn)]"
        />
        Remember me on this device
      </label>
      <div className="text-center">
        <button onClick={() => handleNavigate('choose-plan')} className="text-[color:var(--color-text)] hover:opacity-80 font-medium">New user? Choose your plan</button>
      </div>
      <div className="mt-6 text-xs text-[color:var(--color-text)] text-center">
        <p>Your financial data is stored securely in your personal Google Drive.</p>
      </div>
    </div>
  );

  const ChoosePlanView = () => (
    <div>
      <div className="text-center mb-6">
        <img src={logo} alt="Business Finance Tracker" className="h-16 mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-[color:var(--color-text)] mb-2">Business Finance Tracker</h1>
        <p className="text-[color:var(--color-text)] mb-4">Complete financial management for self-employed people and small companies. Track income, manage expenses, calculate taxes, and grow your business.</p>
        <h2 className="text-2xl font-bold text-[color:var(--color-text)] mb-2">Choose Your Plan</h2>
        <p className="text-[color:var(--color-text)]">Start with the free premium trial or choose the basic plan</p>
      </div>
      <label className="flex items-center justify-center gap-2 mb-6">
        <input
          type="checkbox"
          checked={rememberMe}
          onChange={(e) => onRememberChange(e.target.checked)}
          className="w-4 h-4 rounded border-gray-300 accent-[color:var(--color-warn)]"
        />
        <span className="text-sm text-[color:var(--color-text)]">Remember me on this device</span>
      </label>
      <div className="flex flex-col">
      <div className="order-2 border-2 border-[color:var(--color-info)] bg-[color:var(--color-info)] rounded-lg p-4 mb-4">
        <div className="text-center mb-4">
          <h3 className="text-lg font-bold text-[color:var(--color-text)] mb-1">Free Plan</h3>
          <div className="text-2xl font-bold text-[color:var(--color-text)]">€0</div>
          <div className="text-sm text-[color:var(--color-text)]">Forever</div>
        </div>
        <button
          onClick={() => setExpandedPlan(expandedPlan === 'free' ? null : 'free')}
          className="w-full flex items-center justify-between px-3 py-2 bg-white hover:brightness-95 rounded-lg mb-4 transition text-left"
        >
          <span className="font-semibold text-[color:var(--color-text)]">View features</span>
          <ChevronDown className={`w-5 h-5 text-[color:var(--color-text)] transition-transform ${expandedPlan === 'free' ? 'rotate-180' : ''}`} />
        </button>
        {expandedPlan === 'free' && (
          <ul className="space-y-3 mb-4">
            {[
              ['Add transactions manually', 'Type in what you earned and spent'],
              ["See how much you're really making", 'Automatic profit & tax calculations'],
              ['Download reports anytime', 'Easily share with your accountant'],
              ['Know what to charge', 'Use our built-in pricing helper'],
              ['Set money goals & track progress', "Know if you're hitting your targets"],
              ['Keep your data safe', 'Syncs with Google Drive']
            ].map(([title, desc], idx) => (
              <li key={idx} className="flex items-start text-sm text-[color:var(--color-text)]">
                <div className="w-6 h-6 bg-warn-gradient rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <Check className="w-4 h-4 text-[color:var(--color-text)]" />
                </div>
                <div>
                  <span className="font-semibold text-[color:var(--color-text)]">{title}</span>
                  <br />
                  <span className="text-[color:var(--color-text)]">{desc}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
        <button
          onClick={handleCreateFreeUser}
          className="w-full bg-white text-[color:var(--color-text)] px-4 py-3 rounded-lg hover:brightness-95 transition font-medium"
        >
          Start with Free Plan
        </button>
      </div>
      <div className="order-1 border-2 border-[color:var(--color-accent)] rounded-lg p-4 mb-4 bg-[color:var(--color-accent)]">
        <div className="text-center mb-4">
          <h3 className="text-lg font-bold text-[color:var(--color-text)] mb-1">Premium Trial</h3>
          <div className="text-2xl font-bold text-[color:var(--color-text)]">Free</div>
          <div className="text-sm text-[color:var(--color-text)] font-medium">14 days, then €15/month (VAT incl.)</div>
        </div>
        <button onClick={() => setExpandedPlan(expandedPlan === 'premium' ? null : 'premium')} className="w-full flex items-center justify-between px-3 py-2 bg-warn-gradient hover:brightness-95 rounded-lg mb-4 transition text-left">
          <span className="font-semibold text-[color:var(--color-text)]">View all features</span>
          <ChevronDown className={`w-5 h-5 text-[color:var(--color-text)] transition-transform ${expandedPlan === 'premium' ? 'rotate-180' : ''}`} />
        </button>
        {expandedPlan === 'premium' && (
          <ul className="space-y-3 mb-4">
            {[
              ['Everything in Free Plan', ''],
              ['Talk instead of typing', 'Add income or expenses just by speaking'],
              ['Snap & save receipts', "Take a picture, and we'll fill in the details for you"],
              ['See your money clearly', 'Simple charts that show where your money goes'],
              ['Track growth easily', "Understand what's working in your business"]
            ].map(([title, desc], idx) => (
              <li key={idx} className="flex items-start text-sm text-[color:var(--color-text)]">
                <div className="w-6 h-6 bg-warn-gradient rounded-full flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                  <Check className="w-4 h-4 text-[color:var(--color-text)]" />
                </div>
                <div>
                  <span className="font-semibold text-[color:var(--color-text)]">{title}</span>
                  {desc && (
                    <>
                      <br />
                      <span className="text-[color:var(--color-text)]">{desc}</span>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
        <button onClick={handleCreateTrialUser} className="w-full bg-[color:var(--color-warn)] text-[color:var(--color-text)] px-4 py-3 rounded-lg hover:brightness-95 transition font-medium flex items-center justify-center gap-2">
          <Zap className="w-4 h-4" />
          Start Free Trial
        </button>
      </div>
      </div>
      <div className="text-center">
        <button onClick={() => handleNavigate('signin')} className="text-[color:var(--color-text)] hover:opacity-80 text-sm">Already have an account? Sign in</button>
      </div>
    </div>
  );

  const PricingView = () => (
    <div>
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-[color:var(--color-warn)] rounded-full flex items-center justify-center mx-auto mb-4">
          <Zap className="w-8 h-8 text-[color:var(--color-text)]" />
        </div>
        <h2 className="text-2xl font-bold text-[color:var(--color-text)] mb-2">Start Your Free Trial</h2>
        <p className="text-[color:var(--color-text)]">Get 14 days of Pro features, absolutely free</p>
      </div>
      <div className="bg-white rounded-lg p-4 mb-6 border border-gray-200">
        <h3 className="font-semibold text-[color:var(--color-text)] mb-3 flex items-center">
          <Star className="w-5 h-5 text-[color:var(--color-text)] mr-2" />
          What's included in your trial:
        </h3>
        <ul className="space-y-2">
          {trialFeatures.map((feature, index) => (
            <li key={index} className="flex items-center text-sm text-[color:var(--color-text)]">
              <Check className="w-4 h-4 text-[color:var(--color-text)] mr-2 flex-shrink-0" />
              {feature}
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-white border-2 border-[color:var(--color-warn)] rounded-lg p-4 mb-6">
        <div className="text-center">
          <div className="text-sm text-[color:var(--color-text)] mb-1">After trial ends:</div>
          <div className="text-2xl font-bold text-[color:var(--color-text)]">€15<span className="text-sm font-normal text-[color:var(--color-text)]">/month</span></div>
          <div className="text-sm text-[color:var(--color-text)]">or downgrade to basic plan</div>
        </div>
      </div>
      <button onClick={handleGoogleSignIn} className="w-full bg-[color:var(--color-warn)] text-[color:var(--color-text)] px-6 py-4 rounded-lg hover:brightness-95 transition flex items-center justify-center gap-3 mb-4 font-semibold">
        <svg className="w-6 h-6" viewBox="0 0 24 24"><path fill="currentColor" d="M12.545,12.151c0,1.054.855,1.909,1.909,1.909h3.536c-.607,1.972-2.405,3.404-4.536,3.404-2.626,0-4.754-2.128-4.754-4.754s2.128-4.754,4.754-4.754c1.26,0,2.405.492,3.255,1.29l1.534-1.534C16.455,6.892,14.426,6,12.545,6,8.901,6,6,8.901,6,12.545s2.901,6.545,6.545,6.545c4.017,0,6.545-2.927,6.545-6.545V11.5h-6.545v.651z"/></svg>
        Start Free Trial
      </button>
      <div className="text-center text-xs text-[color:var(--color-text)] space-y-1">
        <p>• No credit card required</p>
        <p>• Cancel anytime</p>
        <p>• Your data stays in your Google Drive</p>
      </div>
      <div className="text-center mt-4">
        <button onClick={() => handleNavigate('signin')} className="text-[color:var(--color-text)] hover:opacity-80 text-sm">Already have an account? Sign in</button>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-md mx-auto relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} aria-label="Close plan selection" className="absolute top-4 right-4 text-[color:var(--color-text)] hover:opacity-80 transition z-10">
          <X className="w-6 h-6" />
        </button>
        <div className="p-6 pt-14">
          {currentView === 'signin' && <SignInView />}
          {currentView === 'choose-plan' && <ChoosePlanView />}
          {currentView === 'pricing' && <PricingView />}
        </div>
      </div>
    </div>
  );
};

export default EnhancedAuthCheck;
